// Shared designations source of truth for every profile create/edit surface.
// Mirrors backend ALLOWED_DESIGNATIONS (app/core/designations.py).
//
// CMT is LOCKED: it is authoritative from CMT Association records, never
// user-editable, and shown only while the person is a live active charterholder.
// The backend strips CMT from any write and re-computes it on read, so the UI
// must not offer it as a selectable chip — render it as a read-only badge only.

export const ALL_DESIGNATIONS = [
    "CMT",
    "CFA",
    "CAIA",
    "CIPM",
    "CQF",
    "FRM",
    "PRM",
    "CTP",
    "CFP",
    "ChFC",
    "CWM",
    "CPA",
    "ACCA",
    "CMA",
    "FMVA",
    "CEFA",
    "MSTA",
    "CIM",
    "FCSI",
    "ABFPSM",
    "PhD",
    "MBA",
    "MFTA",
    "CFTe",
    "Other",
] as const;

export type Designation = (typeof ALL_DESIGNATIONS)[number];

// Locked credentials the user/staff cannot edit — synced from CRM, computed live.
export const LOCKED_DESIGNATIONS: readonly string[] = ["CMT"];

export function isLockedDesignation(value: string): boolean {
    return LOCKED_DESIGNATIONS.some((d) => d.toLowerCase() === value.trim().toLowerCase());
}

// The chips a user may actually toggle (everything except locked CMT).
export const SELECTABLE_DESIGNATIONS: readonly string[] = ALL_DESIGNATIONS.filter(
    (d) => !isLockedDesignation(d),
);

// Parse a stored CSV (e.g. "CMT, CFA, Other") into recognised selections + free text.
export function parseDesignations(
    designations?: string,
    designationOther?: string,
): { selections: string[]; other: string; lockedPresent: string[] } {
    const parts = (designations || "")
        .split(/[;,]/)
        .map((v) => v.trim())
        .filter(Boolean);

    const canonicalMap = new Map(ALL_DESIGNATIONS.map((d) => [d.toLowerCase(), d as string]));

    const selections: string[] = [];
    const lockedPresent: string[] = [];
    for (const value of parts) {
        const canonical = canonicalMap.get(value.toLowerCase());
        if (!canonical) continue;
        if (isLockedDesignation(canonical)) {
            if (!lockedPresent.includes(canonical)) lockedPresent.push(canonical);
        } else if (!selections.includes(canonical)) {
            selections.push(canonical);
        }
    }

    const other = (designationOther || "").trim();
    if (other && !selections.includes("Other")) selections.push("Other");

    return { selections, other, lockedPresent };
}

// Build the CSV to send to the backend from selectable chips only (CMT excluded).
export function buildDesignationsPayload(selections: string[]): string {
    return selections
        .filter((v) => !isLockedDesignation(v))
        .filter((v) => SELECTABLE_DESIGNATIONS.includes(v))
        .join(", ");
}
